/**
 * Preview-and-share modal for the Incident Brief Share Card.
 *
 * Shows the polished card with all 8 fields derived from the incident,
 * then a "One-click share" row with WhatsApp / Teams / Slack / Email
 * plus copy-text, copy-link, and download-as-PNG actions.
 */
import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, Link2, Send, Share2, Mail } from "lucide-react";
import { toast } from "sonner";
import IncidentBriefShareCard from "./IncidentBriefShareCard";
import { buildShareBrief, briefToShareText } from "@/lib/incidentBrief";

// -----------------------------
// Brand-coloured platform icons (inline SVG so html-to-image captures them).
// -----------------------------
function WhatsAppIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="11" fill="#25D366" />
      <path
        d="M16.6 14.3c-.3-.1-1.6-.8-1.8-.9-.2-.1-.4-.1-.6.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.1.2-.3.3-.4.1-.2 0-.3 0-.5 0-.1-.6-1.4-.8-1.9-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2 0 1.3.9 2.5 1.1 2.7.1.2 1.8 2.8 4.5 3.9.6.3 1.1.4 1.5.5.6.2 1.2.2 1.6.1.5-.1 1.6-.6 1.8-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.3z"
        fill="white"
      />
    </svg>
  );
}

function TeamsIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <rect x="9" y="6" width="11" height="11" rx="1.5" fill="#5059C9" />
      <text x="14.5" y="14.5" textAnchor="middle" fontSize="8" fontWeight="700" fill="white" fontFamily="Inter, sans-serif">T</text>
      <circle cx="6" cy="7.5" r="2.5" fill="#7B83EB" />
      <rect x="3" y="11" width="6" height="7" rx="1" fill="#7B83EB" />
    </svg>
  );
}

function SlackIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <rect x="4" y="10" width="6" height="2.4" rx="1.2" fill="#E01E5A" />
      <rect x="10" y="4" width="2.4" height="6" rx="1.2" fill="#E01E5A" />
      <rect x="14" y="11.6" width="6" height="2.4" rx="1.2" fill="#2EB67D" />
      <rect x="11.6" y="14" width="2.4" height="6" rx="1.2" fill="#2EB67D" />
      <rect x="14" y="4" width="2.4" height="6" rx="1.2" fill="#ECB22E" />
      <rect x="14" y="10" width="6" height="2.4" rx="1.2" fill="#ECB22E" />
      <rect x="4" y="14" width="6" height="2.4" rx="1.2" fill="#36C5F0" />
      <rect x="7.6" y="14" width="2.4" height="6" rx="1.2" fill="#36C5F0" />
    </svg>
  );
}

function EmailIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <rect x="3" y="5.5" width="18" height="13" rx="2" fill="#3B82F6" />
      <path d="M3.6 6.4l8.4 6.4 8.4-6.4" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShareButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      type="button"
      className="flex items-center gap-2 px-3 sm:px-4 h-11 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-slate-300 transition-all text-[13px] sm:text-sm font-semibold text-slate-800"
    >
      <Icon className="h-5 w-5" />
      {label}
    </button>
  );
}

// -----------------------------
// Main dialog
// -----------------------------
export default function IncidentBriefShareDialog({ incident, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const brief = buildShareBrief(incident);

  // ---------- Share actions ----------
  const shareText = briefToShareText(brief);
  const shareUrl = brief?.full_link_href || "";

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Incident brief copied to clipboard");
    } catch (e) {
      toast.error("Couldn't copy. Select the card and copy manually.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Incident link copied");
    } catch (e) {
      toast.error("Couldn't copy link.");
    }
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#f8fafc",
        style: { padding: "16px" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `creaiops-incident-brief-${incident?.id?.slice(0, 8) || "card"}.png`;
      a.click();
      toast.success("Share card downloaded");
    } catch (e) {
      toast.error("Couldn't generate image. Try again or copy text.");
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = () => {
    const msg = `${shareText}`;
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const openTeams = () => {
    const params = new URLSearchParams({
      msgText: shareText,
      href: shareUrl,
    });
    window.open(
      `https://teams.microsoft.com/share?${params.toString()}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const openSlack = async () => {
    // Slack has no first-party "share via URL" web flow. Best path: copy
    // formatted text + a deep link that opens Slack if installed, else
    // its web app.
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success(
        "Incident brief copied. Pick a Slack channel and paste.",
      );
    } catch {
      toast.message("Copy the brief and paste into Slack.");
    }
    window.open("https://slack.com/app_redirect?app=A0F7XDU4G", "_blank", "noopener,noreferrer");
  };

  const openEmail = () => {
    const subject = `CREAIOps Incident Brief — ${brief?.title || "Incident"}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`;
  };

  // ---------- Trigger ----------
  const defaultTrigger = (
    <Button
      variant="outline"
      size="sm"
      data-testid="open-brief-share-trigger"
      className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
      disabled={!brief}
      title={brief ? "Open shareable Incident Brief" : "Available after analysis completes"}
    >
      <Share2 className="mr-1.5 h-4 w-4" strokeWidth={2} />
      Share Brief
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent
        data-testid="incident-brief-share-dialog"
        className="max-w-xl bg-slate-50 border border-slate-200 p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 bg-white">
          <DialogTitle className="text-slate-900 font-heading font-bold tracking-tight text-base">
            Incident Brief — preview &amp; share
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 sm:px-6 py-5 max-h-[78vh] overflow-y-auto">
          {brief ? (
            <>
              <IncidentBriefShareCard ref={cardRef} brief={brief} />

              {/* One-click share row */}
              <div
                data-testid="brief-share-row"
                className="mt-5 flex items-center gap-3 text-[12px] text-slate-500"
              >
                <div className="flex-1 h-px bg-slate-200" />
                <span className="inline-flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5 text-sky-500" strokeWidth={2} />
                  <span className="font-semibold text-slate-600">One-click share</span>
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <ShareButton
                  icon={WhatsAppIcon}
                  label="WhatsApp"
                  onClick={openWhatsApp}
                  testid="share-whatsapp"
                />
                <ShareButton
                  icon={TeamsIcon}
                  label="Teams"
                  onClick={openTeams}
                  testid="share-teams"
                />
                <ShareButton
                  icon={SlackIcon}
                  label="Slack"
                  onClick={openSlack}
                  testid="share-slack"
                />
                <ShareButton
                  icon={EmailIcon}
                  label="Email"
                  onClick={openEmail}
                  testid="share-email"
                />
              </div>

              {/* Secondary actions */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyText}
                  data-testid="brief-copy-text"
                  className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                  Copy text
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  data-testid="brief-copy-link"
                  className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
                >
                  <Link2 className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                  Copy link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadPng}
                  disabled={busy}
                  data-testid="brief-download-png"
                  className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                  {busy ? "Generating…" : "Download PNG"}
                </Button>
                <a
                  href={`mailto:?subject=${encodeURIComponent("CREAIOps Incident Brief")}&body=${encodeURIComponent(shareText)}`}
                  data-testid="brief-mailto-fallback"
                  className="hidden"
                  aria-hidden
                >
                  <Mail className="h-3.5 w-3.5" />
                </a>
              </div>
            </>
          ) : (
            <div
              data-testid="brief-not-ready"
              className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 leading-relaxed"
            >
              The Incident Brief becomes available after analysis completes.
              Re-run analysis once the agents have produced a top action and a
              CRE score.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
